import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, RefreshCw } from "lucide-react";
import KickoffPanel from "./ProjectDetail/panels/KickoffPanel";
import { apiService } from "../services/api";
import { useAuth } from "./Auth";
import useKickoffSession from "./Kickoff/useKickoffSession";

const ProjectKickoffPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshDebounceRef = useRef(null);
  const normalizedProjectId = useMemo(() => {
    const parsed = Number(projectId);
    return Number.isNaN(parsed) ? null : parsed;
  }, [projectId]);
  const { user, getAuthHeaders } = useAuth();
  const {
    presenceCount,
    connectedUsers,
    realtimeConnected,
    kickoffError,
    refreshToken,
    notifyRefresh,
  } = useKickoffSession({
    projectId: normalizedProjectId,
    user,
    getAuthHeaders,
  });

  const buildKickoffParticipants = useCallback(
    (projectData, focusEntries, employeesData) => {
      if (!projectData?.id) return [];
      const token = `@[project:${projectData.id}|`;
      const byId = new Map();
      (focusEntries || []).forEach((entry) => {
        if (!entry?.focus_text || !entry.focus_text.includes(token)) return;
        const employee = (employeesData || []).find(
          (emp) => emp.id === entry.employee_id,
        );
        if (employee) {
          byId.set(employee.id, employee);
        }
      });
      return Array.from(byId.values()).sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || ""),
      );
    },
    [],
  );

  const loadKickoffData = useCallback(async ({ silent = false } = {}) => {
    if (!normalizedProjectId) {
      if (!silent) {
        setError("Projet introuvable.");
        setLoading(false);
      }
      return;
    }
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const [
        projectData,
        objectivesData,
        iterationsData,
        employeesData,
      ] = await Promise.all([
        apiService.get(`/projects/${normalizedProjectId}`),
        apiService.get(`/projects/${normalizedProjectId}/objectives`),
        apiService.get(`/projects/${normalizedProjectId}/iterations`),
        apiService.get(`/employees`).catch((employeesError) => {
          console.error("Error fetching employees for kickoff:", employeesError);
          return [];
        }),
      ]);
      let focusEntries = [];
      if (projectData?.circle_id) {
        try {
          const focusData = await apiService.get(
            `circle-focus?parent_id=${projectData.circle_id}`,
          );
          focusEntries = Array.isArray(focusData) ? focusData : [];
        } catch (focusError) {
          console.error("Error fetching role focus for kickoff:", focusError);
        }
      }
      const kickoffParticipants = buildKickoffParticipants(
        projectData,
        focusEntries,
        employeesData,
      );
      setProject(projectData || null);
      setObjectives(Array.isArray(objectivesData) ? objectivesData : []);
      setIterations(Array.isArray(iterationsData) ? iterationsData : []);
      setParticipants(kickoffParticipants);
    } catch (err) {
      console.error("Error loading kickoff workspace:", err);
      setError(err?.message || "Erreur lors du chargement du kickoff.");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [normalizedProjectId, buildKickoffParticipants]);

  useEffect(() => {
    loadKickoffData();
  }, [loadKickoffData]);

  useEffect(() => {
    if (!refreshToken) return;
    loadKickoffData({ silent: true });
  }, [refreshToken, loadKickoffData]);

  useEffect(() => {
    return () => {
      if (refreshDebounceRef.current) {
        clearTimeout(refreshDebounceRef.current);
      }
    };
  }, []);

  const handleKickoffSync = useCallback(
    (scope) => {
      if (notifyRefresh) {
        notifyRefresh(scope);
      }
      if (refreshDebounceRef.current) {
        clearTimeout(refreshDebounceRef.current);
      }
      refreshDebounceRef.current = setTimeout(() => {
        loadKickoffData({ silent: true });
      }, 300);
    },
    [loadKickoffData, notifyRefresh]
  );

  const handleBack = () => {
    if (normalizedProjectId) {
      navigate(`/project/${normalizedProjectId}`);
    } else {
      navigate("/projects");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto w-full max-w-full px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour projet
            </button>
            <div className="text-sm text-slate-500">
              {project?.name ? `Kickoff • ${project.name}` : "Kickoff projet"}
            </div>
          </div>
          <button
            onClick={loadKickoffData}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Rafraîchir
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}
        {!error && kickoffError && (
          <div className="mb-4 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            <AlertCircle className="h-5 w-5" />
            <span>{kickoffError}</span>
          </div>
        )}

        {loading && (
          <div className="flex min-h-[320px] items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-white text-slate-500">
            Chargement du kickoff...
          </div>
        )}

        {!loading && !error && project && (
          <KickoffPanel
            projectId={normalizedProjectId}
            project={project}
            objectives={objectives}
            iterations={iterations}
            participants={participants}
            presenceCount={presenceCount}
            connectedUsers={connectedUsers}
            realtimeConnected={realtimeConnected}
            refreshToken={refreshToken}
            onKickoffSync={handleKickoffSync}
          />
        )}
      </div>
    </div>
  );
};

export default ProjectKickoffPage;
